'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { User, Mail } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { FormInput } from '@/components/forms/FormInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { Alert } from '@/components/common/Alert';
import { ImageUpload } from '@/components/common/ImageUpload';

import { playerService } from '@/services/api/players';
import { handleError } from '@/lib/utils/error';
import type { Pool, Tournament, Player, PlayerRole } from '@/lib/types/auction';
import { cn } from '@/lib/utils';

/** 🔹 Schema Validation */
const playerSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters').max(50),
  email: z
    .optional(z.string().email('Invalid email address'))
    .transform((email) => email?.toLowerCase()),
  role: z.enum(['player'] as const),
  poolId: z.string().min(1, 'Please select a pool'),
});

export type PlayerFormData = z.infer<typeof playerSchema>;

interface PlayerRegistrationFormProps {
  pools: Pool[];
  tournament: Tournament;
  onSuccess: (player: Player) => void;
  className?: string;
}

export function PlayerRegistrationForm({
  pools,
  tournament,
  onSuccess,
  className,
}: PlayerRegistrationFormProps) {
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetImage, setResetImage] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<PlayerFormData>({
    resolver: zodResolver(playerSchema),
  });

  useEffect(() => {
    return () => {
      if (profileImage?.startsWith('blob:')) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  /** 🔹 Form Submission */
  const onSubmit = async (data: PlayerFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      const selectedPool = pools.find((p) => p.id === data.poolId);
      if (!selectedPool) {
        throw new Error('Selected pool not found.');
      }

      const player = await playerService.create(tournament.id, {
        player: {
          firstName: data.name.trim().split(' ')[0],
          lastName: data.name.trim().split(' ').slice(1).join(' '),
          email: data.email,
          profilePicture: profileImage,
        },
        role: data.role as PlayerRole,
        poolId: data.poolId,
        tournamentId: tournament.id,
      });

      onSuccess(player);
      reset();
      setProfileImage(undefined);
      setResetImage(true); // Set reset flag to true after successful submission

      // Reset the reset flag after a short delay to allow for next upload
      setTimeout(() => {
        setResetImage(false);
      }, 100);
    } catch (err) {
      setError(handleError(err, 'Player Registration'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('space-y-4', className)}
    >
      {error && <Alert type="error" message={error} />}

      <ImageUpload
        initialImage={profileImage}
        tag="player"
        onChange={setProfileImage}
        onError={setError}
        shape="circle"
        fallbackText={watch('name') || 'Player'}
        reset={resetImage}
      />

      <div className="space-y-4">
        <FormInput
          label="Player Name"
          name="name"
          register={register}
          error={errors.name?.message}
          leftIcon={<User className="h-4 w-4 text-gray-500" />}
          placeholder="Enter player's full name"
        />

        <FormSelect
          label="Pool"
          name="poolId"
          register={register}
          error={errors.poolId?.message}
          options={[
            { value: '', label: 'Select Pool' },
            ...pools.map((pool) => ({ value: pool.id, label: pool.name })),
          ]}
        />

        <FormSelect
          label="Role"
          name="role"
          register={register}
          error={errors.role?.message}
          options={[
            { value: '', label: 'Select Role' },
            { value: 'player', label: 'Player' },
          ]}
        />

        <FormInput
          label="Email"
          type="email"
          name="email"
          register={register}
          error={errors.email?.message}
          leftIcon={<Mail className="h-4 w-4 text-gray-500" />}
          placeholder="player@example.com"
        />
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-primary-500 to-accent-blue text-white transition-all hover:shadow-md sm:w-auto"
        >
          {isLoading ? 'Creating Player...' : 'Create Player'}
        </Button>
      </div>
    </form>
  );
}
